import * as React from 'react';
import { cn } from '../../../utils';
import { HorizontalScrollList } from '../../molecules/horizontal-scroll-list/horizontal-scroll-list';
import { TABBED_GRID_LIST_CLASS, TABBED_GRID_SECTION_CLASS } from './tabbed-grid.constants';
import type { ITabbedGridProps, TabbedGridTabId } from './tabbed-grid.types';

/**
 * Generic "tabs on top, responsive grid below" layout primitive.
 *
 * Renders a horizontally-scrollable row of consumer-rendered tabs and, beneath
 * it, a responsive grid of consumer-rendered items belonging to the active tab.
 * `TabbedGrid` owns layout (section wrapper, tab-list scroll container, grid
 * breakpoints, gaps) and selection-state plumbing; it has no opinions about
 * what tabs or items look like — both are passed as `React.ComponentType` props
 * (`TabComponent`, `ItemComponent`) and the consumer renders everything.
 *
 * The active tab is resolved from `selectedTabId` via `getTabId`, falling back
 * to `tabs[0]` when no match. Returns `null` when `tabs` is empty.
 */
function TabbedGridInner<TTab, TItem, TId extends TabbedGridTabId = TabbedGridTabId>(
	props: ITabbedGridProps<TTab, TItem, TId>
): JSX.Element | null {
	const {
		tabs,
		getTabId,
		getItems,
		getItemKey,
		selectedTabId,
		onSelectedTabIdChange,
		TabComponent,
		ItemComponent,
		className,
		'data-testid': dataTestId,
	} = props;

	const activeTab: TTab | undefined = React.useMemo(() => {
		if (!tabs.length) {
			return undefined;
		}
		const found: TTab | undefined = tabs.find(tab => getTabId(tab) === selectedTabId);
		return found ?? tabs[0];
	}, [tabs, selectedTabId, getTabId]);

	const TabAdapter: React.VoidFunctionComponent<{ item: TTab }> = ({ item }) => {
		const isSelected: boolean = activeTab !== undefined && getTabId(item) === getTabId(activeTab);
		const handleSelect = (): void => {
			onSelectedTabIdChange(getTabId(item));
		};
		return <TabComponent tab={item} isSelected={isSelected} onSelect={handleSelect} />;
	};

	if (!tabs.length || !activeTab) {
		return null;
	}

	const items: TItem[] = getItems(activeTab);

	return (
		<section className={cn(TABBED_GRID_SECTION_CLASS, className)} data-testid={dataTestId}>
			<HorizontalScrollList<TTab> items={tabs} ItemComponent={TabAdapter} />
			<ul className={TABBED_GRID_LIST_CLASS}>
				{items.map(item => (
					<li key={getItemKey(activeTab, item)}>
						<ItemComponent tab={activeTab} item={item} />
					</li>
				))}
			</ul>
		</section>
	);
}

TabbedGridInner.displayName = 'TabbedGrid';

export const TabbedGrid = TabbedGridInner as <TTab, TItem, TId extends TabbedGridTabId = TabbedGridTabId>(
	props: ITabbedGridProps<TTab, TItem, TId>
) => JSX.Element | null;
